import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { NotificationList } from './notification-list'
import { useAllNotifications, useMarkAllRead, useNotifications } from './queries'

export function NotificationBell() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [allOpen, setAllOpen] = useState(false)
  const { data: items = [] } = useNotifications()
  const markAllRead = useMarkAllRead()
  const unread = items.filter((n) => !n.read).length

  // v1: opening marks everything read; the badge clears once it's closed
  const onOpenChange = (next: boolean) => {
    setOpen(next)
    if (next && unread > 0) markAllRead.mutate()
  }

  return (
    <>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" aria-label={t('notifications.title')}>
            <Bell />
            {unread > 0 && (
              <span className="figures absolute top-0 right-0 flex h-3.5 min-w-3.5 ring-2 ring-card items-center justify-center rounded-full bg-primary px-0.5 text-[9px] leading-none font-semibold text-primary-foreground">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-0">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h2 className="text-sm font-semibold">{t('notifications.title')}</h2>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <NotificationList items={items.slice(0, 8)} />
          </div>
          <div className="border-t p-1">
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => {
                setOpen(false)
                setAllOpen(true)
              }}
            >
              {t('v2.notifications.viewAll')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <AllNotificationsDialog open={allOpen} onOpenChange={setAllOpen} />
    </>
  )
}

function AllNotificationsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation()
  const query = useAllNotifications(open)
  const items = query.data?.pages.flat() ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-lg">
        <DialogHeader className="border-b px-4 py-3">
          <DialogTitle className="text-base">{t('notifications.all')}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh]">
          {query.isPending ? (
            <p className="p-4 text-sm text-muted-foreground">{t('notifications.loading')}</p>
          ) : (
            <NotificationList items={items} />
          )}
          {query.hasNextPage && (
            <div className="p-3">
              <Button variant="outline" className="w-full" disabled={query.isFetchingNextPage} onClick={() => query.fetchNextPage()}>
                {t('common.loadMore')}
              </Button>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
